import { useSelector } from "react-redux";
import { AppRootState } from "../../redux/reducers";
import { Link } from 'react-router-dom';
import { validateDateOfBirth } from "../../common/utils";
import "./styles/main.css";

function capitalizeFirst(str: string | null | undefined): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const Dashboard = () => {
  const userData: any = useSelector((state: AppRootState) => state.userData);
  
  // Check if date of birth is missing or invalid
  const isDateOfBirthMissing = !userData.birthdate || userData.birthdate.trim() === '';
  const isDateOfBirthInvalid = userData.birthdate && !validateDateOfBirth(userData.birthdate);
  const showDateOfBirthNotification = isDateOfBirthMissing || isDateOfBirthInvalid;

  return (
    <div className="w-full bg-gradient-to-br">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-brand-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-brand-gray-900 mb-2">
              Hey there, <span className="text-brand-blue-500">{capitalizeFirst(userData.given_name)}</span>- we’re so glad you came!
            </h1>
            <p className="text-xl text-brand-gray-600 max-w-2xl mx-auto pt-3">
            Whether you’re here to register your kit or view your results, <span className="bg-brand-blue-100 rounded-md">Jack At Home</span> is the easiest and most convenient way to confirm the success of your vasectomy.
            </p>
          </div>
        </div>
      </div>

      {/* Date of Birth Notification */}
      {showDateOfBirthNotification && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-gradient-to-r from-brand-blue-50 to-brand-blue-100 rounded-2xl border border-brand-blue-200 p-6 shadow-sm">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-brand-blue-200">
                  <svg className="h-6 w-6 text-brand-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-brand-gray-900">
                    {isDateOfBirthMissing ? 'Complete Your Profile' : 'Profile Update Needed'}
                  </h3>
                  <div className="flex items-center text-sm text-brand-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    Quick action needed
                  </div>
                </div>
                <p className="mt-2 text-brand-gray-700 leading-relaxed">
                  {isDateOfBirthMissing 
                    ? `Hi ${capitalizeFirst(userData.given_name)}! To get started with your health testing, we just need to add your date of birth to your profile. This helps us provide you with the most accurate results.`
                    : `Hi ${capitalizeFirst(userData.given_name)}! We need to update your date of birth information. This ensures we can provide you with the best possible service.`
                  }
                </p>
                <div className="mt-4 flex items-center space-x-3">
                  <Link 
                    to="/profile-update" 
                    className="inline-flex items-center bg-brand-blue-500 hover:bg-brand-blue-600 text-white font-medium px-5 py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {isDateOfBirthMissing ? 'Add Date of Birth' : 'Update Profile'}
                  </Link>
                  <span className="text-sm text-brand-gray-500">
                    Takes less than 2 minutes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Register New Kit Card */}
          <div className="group">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-brand-gray-200 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-blue-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-brand-blue-200 transition-colors duration-300">
                    <svg className="w-6 h-6 text-brand-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-brand-gray-900">Register New Kit</h2>
                </div>
                
                <p className="text-brand-gray-600 text-lg leading-relaxed mb-8">
                  Start your testing journey by registering your kit. This step is required before sending your sample to our lab to ensure you receive your results.
                </p>
                
                <div className="flex items-center text-sm text-brand-gray-500 mb-8">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Required before sample submission
                </div>
                
                <Link to="/active" className="block">
                  <button className="w-full bg-brand-blue-500 hover:bg-brand-blue-600 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    Get Started
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* View Test Results Card */}
          <div className="group">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-brand-gray-200 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors duration-300">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-brand-gray-900">View Test Results</h2>
                </div>
                
                <p className="text-brand-gray-600 text-lg leading-relaxed mb-8">
                  Access your completed test results and detailed reports. You'll receive an email notification as soon as your results are ready for review.
                </p>
                
                <div className="flex items-center text-sm text-brand-gray-500 mb-8">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Email notifications sent when ready
                </div>
                
                <Link to="/history" className="block">
                  <button className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    View Results
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl shadow-lg border border-brand-gray-200 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-brand-gray-900 mb-4">Need Help?</h3>
            <p className="text-brand-gray-600 text-lg mb-6">
              Our support team is here to assist you with any questions about your testing process or results.
            </p>
            <Link to="/support" className="inline-flex items-center text-brand-blue-500 hover:text-brand-blue-600 font-semibold transition-colors duration-300">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
